import React, {
    createContext,
    useState,
    useContext,
    ReactNode,
    useCallback,
    useMemo,
    useRef,
    useEffect,
} from "react";
import {
    useNodesState,
    useEdgesState,
    useReactFlow,
} from "reactflow";
import { useInRouterContext } from "react-router-dom";
import { useConnect } from "./flow/useConnect";
import { useGraphEdits } from "./flow/useGraphEdits";
import { useDashboardPins, useDashboardPinSave } from "./flow/useDashboardPins";
import { usePlayAll } from "./flow/usePlayAll";
import type { PlayAllState } from "./flow/usePlayAll";
import { useApplyOutput } from "./flow/useApplyOutput";
import { useInteractions } from "./flow/useInteractions";
import { useSelectionTags } from "./flow/useSelectionTags";
import { useCollaborationSync } from "./flow/useCollaborationSync";
import { useInstallSave } from "./flow/useInstallSave";
import { useServerRun } from "./flow/useServerRun";
import { useStepOutcomes } from "./flow/useStepOutcomes";
import { useDashboardRun } from "./flow/useDashboardRun";
import { autoRunTargets, useAutoRun } from "./flow/useAutoRun";
import { CanvasViewAddress, useNotebookView } from "./flow/useNotebookView";
import { NotebookViewContext } from "./flow/notebookViewContext";
import type { IOutput, IInteraction } from "./flow/flowTypes";
import type { FlowContextProps, NodeActionsContextProps } from "./flow/flowContextTypes";
import type { Scenario } from "../utils/scenarios/scenarioModel";
import { DEFAULT_WORKFLOW_NAME } from "../constants";
import { TrillGenerator } from "../TrillGenerator";
import { isStandaloneDashboard } from "../standalone/dashboardPayload";
import { useWorkflowOperations } from "../hook/useWorkflowOperations";
import { useToastContext } from "./ToastProvider";
import { useCollab } from "./CollaborationProvider";
import { useProvenanceContext } from "./ProvenanceProvider";
import { useUserContext } from "./UserProvider";
import { isHostedGuest } from "../components/apiSettings/useHostedGuest";
import { DEFAULT_SAVE_OUTPUT_DATASET } from "../utils/saveOutputDataset";
import { writeNodeSettings, type NodeSettings } from "../utils/nodeSettingsWriters";
import { authApi } from "../utils/authApi";

export type { IOutput, IInteraction, IPropagation, NodeExecOutcome } from "./flow/flowTypes";
export type { NodeActionsContextProps } from "./flow/flowContextTypes";

export const NodeActionsContext = createContext<NodeActionsContextProps>({
    workflowNameRef: { current: "" },
    workflowName: "DefaultWorkflow",
    applyRemoveChanges: () => {},
    setPinForDashboard: () => {},
    allMinimized: 0,
    setAllMinimized: () => {},
    expandStatus: 'expanded',
    setExpandStatus: () => {},
    updateDataNode: () => {},
    updateDefaultCode: () => {},
    workflowGoal: "",
    acceptSuggestion: () => {},
    setWorkflowName: () => {},
});

export const useNodeActionsContext = () => useContext(NodeActionsContext);

export const FlowContext = createContext<FlowContextProps>({
    nodes: [],
    edges: [],
    outputs: [],
    setOutputs: () => { },
    setInteractions: () => { },
    interactionsCallback: () => { },
    applyNewPropagation: () => { },
    addNode: () => { },
    onNodesChange: () => { },
    onEdgesChange: () => { },
    onConnect: () => { },
    isValidConnection: () => true,
    onEdgesDelete: () => { },
    onNodesDelete: () => { },
    setPinForDashboard: () => { },
    dashboardOn: false,
    dashboardLocked: true,
    isDashboardSource: () => false,
    setDashboardLocked: () => { },
    applyNewOutput: () => { },
    hydrateRestoredOutputs: () => { },

    // NEW CODE
    dashboardPins: {},
    workflowNameRef: { current: "" },
    workflowDescriptionRef: { current: "" },
    workflowDescription: "",
    setWorkflowDescription: () => {},
    loading: false,
    suggestionsLeft: 0,
    workflowGoal: "",
    allMinimized: 0,
    expandStatus: 'expanded',
    setWorkflowGoal: () => {},

    applyRemoveChanges: () => { },
    applyReviewedRemovals: () => { },
    setWorkflowName: () => { },
    setAllMinimized: () => { },
    setExpandStatus: () => { },
    eraseWorkflowSuggestions: () => {},
    updateDataNode: () => {},
    flagBasedOnKeyword: () => {},
    updateSubtasks: () => {},
    updateKeywords: () => {},
    updateDefaultCode: () => {},
    applyNodeContent: () => {},
    applyNodeSettings: () => {},
    updateWarnings: () => {},
    cleanCanvas: () => {},
    acceptSuggestion: () => {},
    loadParsedTrill: async () => { },
    packages: [],
    setPackages: () => {},
    addPackage: () => {},
    removePackage: () => {},
    dataflowDatasets: [],
    setDataflowDatasets: () => {},
    pendingInstalls: [],
    beginPendingInstall: () => {},
    endPendingInstall: () => {},
    failPendingInstall: () => {},

    // Project defaults
    projectId: null,
    projectName: "",
    projectDirty: false,
    projectSavedAt: null,
    nodeExecStatus: {},
    viewerMode: "owner",
    workflowCategories: {},
    serverCategories: {},
    scenarios: [],
    autoRun: false,
    renameDataflow: () => false,
    updateDataflowCategories: () => {},
    setScenarios: () => {},
    setAutoRun: () => {},
    saveCurrentProject: async () => {},
    requestProjectSave: async () => {},
    saveAsNewProject: async () => {},
    ensureProjectId: async () => null,
    persistDataflowForInstall: async () => ({ saved: false, failedNodeIds: [] }),
    loadProject: async () => {},
    loadSharedProject: async () => {},
    discardProject: () => {},
    markDirty: () => {},
    markNodeExecuted: () => {},
    markNodeStale: () => {},
    markNodeErrored: () => {},
    playAllNodes: () => {},
    playNodesUpTo: () => {},
    signalNodeExecDone: () => {},
    isRunActive: false,
    serverRunActive: false,
    dashboardStopped: null,
    cancelRun: () => {},
    attachLatestRun: async () => {},
    autoRunAvailable: false,
    requestAutoRun: () => {},
    runDownstream: () => {},
    defaultSaveOutputDataset: false,
    setDefaultSaveOutputDataset: () => {},

    canvasView: "canvas",
    setCanvasView: () => {},
    notebookOn: false,
    notebookContentHeight: 0,
    setNotebookPane: () => {},
    registerNotebookScroller: () => {},
    revealNodes: () => false,
});

/**
 * ``dashboardOn`` is a PROP, not state: it says which route mounted this tree.
 * It used to be a mode the canvas toggled into, which is why the nodes had two
 * sets of coordinates and no URL to share. The dashboard is its own route now,
 * so the flag is decided once, above the provider, and everything that reads it
 * means "I am rendering a dashboard tile" rather than "the canvas is pretending".
 */
const FlowProvider = ({
    children,
    dashboardOn = false,
}: { children: ReactNode; dashboardOn?: boolean }) => {
    const { showToast } = useToastContext();
    // A hosted guest cannot save the dataflow (isHostedGuest, the rule the
    // saves themselves apply as blockGuestSaves). Its runs stay in the browser,
    // and the saves Curio makes on its own, after an output lands or a pin
    // changes, are skipped without a word: only a save the guest asks for
    // (File > Save, Save a copy) says why it cannot.
    const { user, enableUserAuth, skipProjectPage } = useUserContext();
    const hostedGuest = isHostedGuest(user, enableUserAuth);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [outputs, _setOutputs] = useState<IOutput[]>([]);
    const outputsRef = useRef<IOutput[]>([]);
    const playAllStateRef = useRef<PlayAllState | null>(null);
    // Mirrors "playAllStateRef.current != null" as React state. The ref is what
    // the runner reads; this is what the UI reads. The guard used to live only
    // in the ref, so every play control stayed enabled and a click during (or
    // after a wedged) run silently did nothing (#271).
    const [isRunActive, setIsRunActive] = useState(false);
    // The input each node had when it last emitted through applyNewOutput. The
    // Data Pool never records a success on node.data.output, so this is how
    // playNodesUpTo tells it is current (#479): its input is
    // still the object it emitted for. Every delivery builds a new one.
    const emittedForInputRef = useRef(new Map<string, unknown>());
    const markNodeExecutedRef = useRef<(nodeId: string) => void>(() => {});
    const markNodeStaleRef = useRef<(nodeId: string) => void>(() => {});
    const markNodeErroredRef = useRef<(nodeId: string) => void>(() => {});
    const markDirtyRef = useRef<() => void>(() => {});
    // Saves the project right after a pin changes (assigned below, next to
    // markDirtyRef). Pinning is the one edit whose entire purpose is to change
    // a DIFFERENT page, and that page renders what is on disk, so leaving it to
    // the 30 second auto-save meant "pin a tile, open the dashboard" showed
    // "nothing is pinned yet" for up to half a minute. That reads as a broken
    // feature rather than as a save that has not happened yet.
    const savePinChangeRef = useRef<() => void>(() => {});
    // Set after workflowOps exists; called from applyNewOutput (a genuine runtime
    // output, NOT project load — load writes outputs via setOutputs directly) to
    // auto-install + surface a produced dataset without a manual disk-icon save.
    const scheduleInstallSyncRef = useRef<(nodeId: string) => void>(() => {});
    // Forces the debounced install-save to run NOW (assigned below, next to
    // scheduleInstallSyncRef). The Play-All runner calls it on completion and the
    // provider calls it on unmount so the final burst is never lost to the timer.
    const flushInstallSyncRef = useRef<() => void>(() => {});
    const [defaultSaveOutputDataset, setDefaultSaveOutputDataset] = useState(
        DEFAULT_SAVE_OUTPUT_DATASET,
    );

    // The backend (CURIO_DEFAULT_SAVE_NODE_OUTPUT) is the authoritative source
    // for the workflow-wide default. Read it once from /api/config/public so
    // the per-node "Save output dataset" default (and the save-time gate in
    // buildOutputRefs) matches the deployment's runtime setting rather than the
    // build-time env baked into the bundle. Mirrors CollaborationProvider.
    useEffect(() => {
        // Nothing on a standalone dashboard can save an output: there is no
        // session to save under and no Play to produce one. Asking a server
        // that may not be reachable what the default should be would be a
        // request made purely to answer a question nobody asks.
        if (isStandaloneDashboard()) return;
        let cancelled = false;
        authApi
            .getPublicConfig()
            .then((cfg) => {
                if (!cancelled && typeof cfg?.default_save_node_output === "boolean") {
                    setDefaultSaveOutputDataset(cfg.default_save_node_output);
                }
            })
            .catch(() => {
                /* keep build-time DEFAULT_SAVE_OUTPUT_DATASET fallback */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Collaboration broadcasters. The provider may be a no-op (when
    // --collab is off or no project is loaded), in which case every
    // broadcast call is a noop too. We hold the latest value in a ref
    // so the mutation callbacks below don't have to list `collab` in
    // their dependency arrays and risk re-creating themselves on every
    // presence change.
    const collab = useCollab();
    const collabRef = useRef(collab);
    collabRef.current = collab;

    // The ref is written now, not when React renders: a save reads the outputs
    // from it, and the save a run's end flushes runs in the tick the run's last
    // output lands. Written in the state updater, the ref did not hold that
    // output yet whenever another update to this provider was pending, so the
    // save left it out and took it out of the dataflow's saved outputs.
    const setOutputs = useCallback((fnOrValue: ((prev: IOutput[]) => IOutput[]) | IOutput[]) => {
        const next = typeof fnOrValue === "function" ? fnOrValue(outputsRef.current) : fnOrValue;
        outputsRef.current = next;
        _setOutputs(next);
    }, []);
    const [interactions, setInteractions] = useState<IInteraction[]>([]);

    const [dashboardPins, setDashboardPins] = useState<any>({}); // {[nodeId] -> boolean}
    // Whether the dashboard's tiles can be moved and resized. Session-only and
    // owner-only: the page starts locked and unlocks for an explicit edit.
    const [dashboardLocked, setDashboardLocked] = useState<boolean>(true);

    const reactFlow = useReactFlow();
    const [loading, setLoading] = useState<boolean>(false);

    const notebook = useNotebookView({ nodes, edges, setNodes, reactFlow, dashboardOn });
    // The app always renders the dataflow inside its router, which keeps the
    // view in the address; some tests render this provider without one.
    const inRouter = useInRouterContext();

    const [workflowName, _setWorkflowName] = useState<string>(DEFAULT_WORKFLOW_NAME);
    const workflowNameRef = React.useRef(workflowName);
    const setWorkflowName = useCallback((data: any) => {
        workflowNameRef.current = data;
        _setWorkflowName(data);
    }, []);

    const [workflowDescription, _setWorkflowDescription] = useState<string>("");
    const workflowDescriptionRef = React.useRef(workflowDescription);
    const setWorkflowDescription = useCallback((data: string) => {
        workflowDescriptionRef.current = data || "";
        _setWorkflowDescription(data || "");
    }, []);

    const initializeProvenance = () => {
        setLoading(true);
        try {
            const empty_trill = TrillGenerator.generateTrill(
                [],
                [],
                workflowNameRef.current,
            );
            TrillGenerator.intializeProvenance(empty_trill);
        } catch (e) {
            console.error("initializeProvenance failed:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        initializeProvenance();
    }, []);

    // The scenarios as they are now, for the save rule a run reads (#662).
    // Assigned once workflowOps exists, as markDirtyRef is.
    const scenariosNowRef = useRef<() => readonly Scenario[]>(() => []);
    const { isDashboardSource, setPinForDashboard } = useDashboardPins({
        reactFlow, setDashboardPins, setNodes, markDirtyRef, savePinChangeRef, showToast, scenariosNowRef,
        hostedGuest,
    });

    // The nodes' Provenance drawers: a run files an entry, and so does an
    // agent's applied change to a node's code.
    const { nodeExecProv, nodeEditProv } = useProvenanceContext();

    const {
        applyNodeContent, addNode, propagateDownstreamInputs, applyOutput, onEdgesDelete, onNodesDelete,
    } = useGraphEdits({
        setNodes, setEdges, reactFlow, workflowNameRef, collabRef, outputsRef, markNodeStaleRef, setOutputs,
        nodeEditProv, markDirtyRef,
    });

    const { onConnect, isValidConnection } = useConnect({
        markDirtyRef, reactFlow, showToast, markNodeStaleRef, applyOutput, setEdges,
        workflowNameRef, collabRef, outputsRef,
    });

    const { cancelRun, signalNodeExecDone, playAllNodes, playNodesUpTo, playNodes } = usePlayAll({
        playAllStateRef, setIsRunActive, flushInstallSyncRef, showToast, reactFlow,
        markNodeErroredRef, setNodes, emittedForInputRef,
    });

    const { applyNewOutput, hydrateRestoredOutputs } = useApplyOutput({
        propagateDownstreamInputs, emittedForInputRef, reactFlow, setOutputs,
        markNodeExecutedRef, scheduleInstallSyncRef, signalNodeExecDone,
    });

    const { applyNewPropagation, interactionsCallback } = useInteractions({
        interactions, setInteractions, nodes, edges, reactFlow, setNodes,
    });

    // #662: a view's new selection reaches the selection tags that read it,
    // and a view drawn again without it empties them (#884).
    const { clearViewTags } = useSelectionTags({ interactions, reactFlow, setNodes, markNodeStaleRef, markDirtyRef });

    useCollaborationSync({
        collab, applyNewOutput, interactionsCallback, applyNewPropagation, setNodes, setEdges,
        takeCanvasPosition: notebook.takeCanvasPosition,
    });
    // NEW CODE

    // Workflow operations extracted into a dedicated hook
    // NOTE: markNodeExecutedRef/markNodeStaleRef are updated here so functions defined earlier
    // (applyNewOutput, onEdgesDelete, onConnect) can access them without stale closures.
    const workflowOps = useWorkflowOperations({
        nodes, edges,
        setNodes, setEdges,
        setOutputs, outputsRef, setInteractions,
        setDashboardPins,
        presentation: dashboardOn,
        setWorkflowName,
        workflowNameRef,
        setWorkflowDescription,
        workflowDescriptionRef,
        onEdgesDelete, onNodesDelete, onNodesChange,
        onConnect, addNode,
        defaultSaveOutputDataset,
    });

    markNodeExecutedRef.current = workflowOps.markNodeExecuted;
    markNodeStaleRef.current = workflowOps.markNodeStale;
    markNodeErroredRef.current = workflowOps.markNodeErrored ?? (() => {});
    markDirtyRef.current = workflowOps.markDirty;
    scenariosNowRef.current = () => workflowOps.scenariosRef?.current ?? workflowOps.scenarios ?? [];

    useDashboardPinSave({
        savePinChangeRef, dashboardOn, workflowOps, showToast, hostedGuest,
    });

    useInstallSave({
        scheduleInstallSyncRef, dashboardOn, reactFlow, defaultSaveOutputDataset,
        isDashboardSource, workflowOps, flushInstallSyncRef, hostedGuest,
    });

    // Runs go to the server for whoever may save the dataflow, since a run
    // saves it first and records its outputs in it. Hosted guests, viewers of
    // a shared dataflow and `--no-project` keep the run in the browser, and so
    // does a canvas with nobody signed in, which cannot save either.
    const runsOnServer =
        !dashboardOn &&
        !isStandaloneDashboard() &&
        !skipProjectPage &&
        user != null &&
        !hostedGuest &&
        workflowOps.viewerMode === "owner";
    // What a step of a run on the server does to its node: one set for the
    // canvas's runs and a dashboard viewer's.
    const stepOutcomes = useStepOutcomes({
        reactFlow, setNodes, applyNewOutput, setOutputs, hydrateRestoredOutputs,
        markNodeErroredRef, workflowNameRef, nodeExecProv,
    });
    const serverRun = useServerRun({
        reactFlow, setNodes, showToast,
        requestProjectSave: workflowOps.requestProjectSave,
        applyNewOutput, setOutputs, hydrateRestoredOutputs, markNodeErroredRef,
        playNodes, playAllStateRef, emittedForInputRef, outputsRef, workflowNameRef,
        nodeExecProv, flushInstallSyncRef,
        surfaceInstallWarnings: workflowOps.surfaceInstallWarnings,
        outcomes: stepOutcomes,
    });
    // #1117: on a dashboard, a change to a control a pinned tile shows reruns
    // the dataflow on the server as a viewer's run, which never changes it.
    const dashboardRun = useDashboardRun({
        enabled: dashboardOn, reactFlow, outcomes: stepOutcomes, outputsRef, showToast, playNodes,
    });
    // #845: where Run All runs on the server, a widget or Parameter change can
    // run the nodes it touched through the same start, once the changes settle.
    // On a dashboard every change does, with no toggle, as a viewer's run (#1117).
    const autoRunner = useAutoRun({
        enabled: dashboardOn || (runsOnServer && !!workflowOps.autoRun),
        runActive: isRunActive || serverRun.serverRunActive || dashboardRun.active,
        reactFlow,
        start: (targets) => {
            if (dashboardOn) void dashboardRun.start();
            else void serverRun.startRun(targets);
        },
    });
    // The Map Editor node's Commit edits: the nodes it names and every node
    // below them, as an automatic run takes them, run now through the same
    // start; in the browser where runs stay there.
    const runDownstream = (nodeIds: readonly string[]) => {
        const targets = autoRunTargets(nodeIds, reactFlow.getNodes(), reactFlow.getEdges());
        if (targets.length === 0) return;
        if (runsOnServer) void serverRun.startRun(targets);
        else playNodesUpTo(targets);
    };
    // An agent's applied settings change: each setting through the setter its
    // node registered, as a pick in the node makes it; the rest into the
    // node's data, a widget's new value leaving what reads it out of date.
    const applyNodeSettings = (nodeId: string, settings: NodeSettings) => writeNodeSettings({
        nodes: () => reactFlow.getNodes(),
        updateDataNode: workflowOps.updateDataNode,
        markNodeStale: workflowOps.markNodeStale,
        markDirty: workflowOps.markDirty,
        requestAutoRun: autoRunner.requestAutoRun,
    }, nodeId, settings);
    // Stop ends whichever run is going, on the server or in the browser, and
    // the automatic run waiting for it.
    const stopRuns = () => { autoRunner.dropWaiting(); cancelRun(); serverRun.stopRun(); };
    // Opening another dataflow leaves a run on the server going.
    const leaveRuns = () => { autoRunner.dropWaiting(); cancelRun(); serverRun.detachRun(); };

    const nodeActionsValue = useMemo<NodeActionsContextProps>(() => ({
        workflowNameRef,
        workflowName,
        applyRemoveChanges: workflowOps.applyRemoveChanges,
        setPinForDashboard,
        allMinimized: workflowOps.allMinimized,
        setAllMinimized: workflowOps.setAllMinimized,
        expandStatus: workflowOps.expandStatus,
        setExpandStatus: workflowOps.setExpandStatus,
        updateDataNode: workflowOps.updateDataNode,
        updateDefaultCode: workflowOps.updateDefaultCode,
        workflowGoal: workflowOps.workflowGoal,
        acceptSuggestion: workflowOps.acceptSuggestion,
        setWorkflowName,
    }), [
        workflowNameRef,
        workflowName,
        workflowOps.applyRemoveChanges,
        setPinForDashboard,
        workflowOps.allMinimized,
        workflowOps.setAllMinimized,
        workflowOps.expandStatus,
        workflowOps.setExpandStatus,
        workflowOps.updateDataNode,
        workflowOps.updateDefaultCode,
        workflowOps.workflowGoal,
        workflowOps.acceptSuggestion,
        setWorkflowName,
    ]);

    return (
        <NodeActionsContext.Provider value={nodeActionsValue}>
        <FlowContext.Provider
            value={{
                nodes,
                edges,
                outputs,
                setOutputs,
                setInteractions,
                interactionsCallback,
                clearSelectionTagsOf: clearViewTags,
                applyNewPropagation,
                addNode,
                onNodesChange,
                onEdgesChange,
                onConnect,
                isValidConnection,
                onEdgesDelete,
                onNodesDelete,
                setPinForDashboard,
                isDashboardSource,
                applyNewOutput,
                hydrateRestoredOutputs,
                playAllNodes: runsOnServer ? () => { void serverRun.startRun(); } : playAllNodes,
                // A run on the server takes one target node, so a list (a
                // scenario's levers) runs in the browser, once no server run
                // is going.
                playNodesUpTo: runsOnServer
                    ? (target: string | readonly string[]) => {
                        if (typeof target === "string") {
                            void serverRun.startRun(target);
                        } else if (serverRun.serverRunActive) {
                            showToast(
                                "A run is already in progress. Wait for it to finish, or cancel it with the stop button.",
                                "info",
                            );
                        } else {
                            playNodesUpTo(target);
                        }
                    }
                    : playNodesUpTo,
                signalNodeExecDone,
                isRunActive,
                serverRunActive: serverRun.serverRunActive || dashboardRun.active,
                dashboardStopped: dashboardRun.stopped,
                cancelRun: stopRuns,
                attachLatestRun: runsOnServer ? serverRun.attachLatestRun : async () => {},
                autoRunAvailable: runsOnServer,
                requestAutoRun: autoRunner.requestAutoRun,
                runDownstream,
                dashboardOpened: dashboardOn ? dashboardRun.opened : undefined,
                dashboardSavedWidgets: dashboardRun.savedWidgets,

                // NEW CODE
                dashboardPins,
                dashboardOn,
                dashboardLocked,
                setDashboardLocked,
                workflowNameRef,
                setWorkflowName,
                workflowDescriptionRef,
                workflowDescription,
                setWorkflowDescription,
                loading,

                ...workflowOps,
                // A project switch must not inherit a run in flight: the guard
                // is provider state, and the provider outlives the dataflow
                // when the user loads another one in place (#271).
                loadProject: async (id: string, options?: { lastResults?: boolean }) => {
                    leaveRuns();
                    return workflowOps.loadProject(id, options);
                },
                loadSharedProject: async (id: string) => { leaveRuns(); return workflowOps.loadSharedProject(id); },
                cleanCanvas: () => { leaveRuns(); workflowOps.cleanCanvas(); },
                discardProject: () => { leaveRuns(); workflowOps.discardProject(); },
                applyNodeContent,
                applyNodeSettings,
                defaultSaveOutputDataset,
                setDefaultSaveOutputDataset,

                canvasView: notebook.canvasView,
                setCanvasView: notebook.setCanvasView,
                notebookOn: notebook.notebookOn,
                notebookContentHeight: notebook.notebookContentHeight,
                setNotebookPane: notebook.setNotebookPane,
                registerNotebookScroller: notebook.registerNotebookScroller,
                revealNodes: notebook.revealNodes,
            }}
        >
            <NotebookViewContext.Provider value={notebook.notebookViewValue}>
                {inRouter && !dashboardOn && <CanvasViewAddress view={notebook.canvasView} />}
                {children}
            </NotebookViewContext.Provider>
        </FlowContext.Provider>
        </NodeActionsContext.Provider>
    );
};

export const useFlowContext = () => {
    const context = useContext(FlowContext);

    if (!context) {
        throw new Error("useFlowContext must be used within a FlowProvider");
    }

    return context;
};

export default FlowProvider;
